import { createPortal } from "react-dom";
import { Box, Tooltip } from "@mui/material";
import { useOverlayTransform } from "./useOverlayTransform";

const SIZE = 320;
const R = 150;
const BASE_Y = R;
const SVG_W = SIZE;
const SVG_H = SIZE / 2 + 24;

function ProtractorSvg() {
  const cx = SIZE / 2;
  const ticks = [];

  for (let deg = 0; deg <= 180; deg += 1) {
    const rad = ((180 - deg) * Math.PI) / 180;
    const isMajor = deg % 10 === 0;
    const isMid = !isMajor && deg % 5 === 0;
    const tickLen = isMajor ? 16 : isMid ? 10 : 5;
    const color = isMajor ? "#0d47a1" : isMid ? "#1976d2" : "#90caf9";
    const width = isMajor ? 1.6 : isMid ? 1.2 : 0.8;
    const x1 = cx + (R - tickLen) * Math.cos(rad);
    const y1 = BASE_Y - (R - tickLen) * Math.sin(rad);
    const x2 = cx + R * Math.cos(rad);
    const y2 = BASE_Y - R * Math.sin(rad);
    ticks.push(
      <line key={`t${deg}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={width} opacity={isMajor ? 1 : 0.85} />
    );
    if (isMajor && deg > 0 && deg < 180) {
      const outerR = R - 22;
      const ox = cx + outerR * Math.cos(rad);
      const oy = BASE_Y - outerR * Math.sin(rad);
      ticks.push(
        <text key={`o${deg}`} x={ox} y={oy} fill="#0d47a1" fontSize="11" fontWeight="700" textAnchor="middle" dominantBaseline="middle">
          {deg}
        </text>
      );
      const innerR = R - 38;
      const ix = cx + innerR * Math.cos(rad);
      const iy = BASE_Y - innerR * Math.sin(rad);
      ticks.push(
        <text key={`i${deg}`} x={ix} y={iy} fill="#1976d2" fontSize="9.5" fontWeight="600" textAnchor="middle" dominantBaseline="middle" opacity={0.85}>
          {180 - deg}
        </text>
      );
    }
  }

  return (
    <svg width={SVG_W} height={SVG_H} viewBox={`0 0 ${SVG_W} ${SVG_H}`} style={{ overflow: "visible", display: "block" }}>
      <path
        d={`M 6 ${BASE_Y} A ${R - 6} ${R - 6} 0 0 1 ${SVG_W - 6} ${BASE_Y}`}
        fill="rgba(255,255,255,0.72)"
        stroke="#1565c0"
        strokeWidth="2"
      />
      <line x1={6} y1={BASE_Y} x2={SVG_W - 6} y2={BASE_Y} stroke="#1565c0" strokeWidth="2" />
      {ticks}
      <line x1={cx - 14} y1={BASE_Y} x2={cx + 14} y2={BASE_Y} stroke="#c62828" strokeWidth="1.5" />
      <line x1={cx} y1={BASE_Y - 14} x2={cx} y2={BASE_Y + 8} stroke="#c62828" strokeWidth="1.5" />
      <circle cx={cx} cy={BASE_Y} r="2.5" fill="#c62828" />
      <circle cx={cx} cy={BASE_Y} r="6" fill="none" stroke="#c62828" strokeWidth="1" opacity={0.5} />
    </svg>
  );
}

export default function VirtualProtractorOverlay({ visible }) {
  const { transform, onPointerDownMove, onPointerDownRotate, onPointerDownScale } = useOverlayTransform();

  if (!visible) return null;

  return createPortal(
    <Box
      sx={{
        position: "fixed",
        left: transform.x,
        top: transform.y,
        transform: `translate(-50%, -50%) rotate(${transform.rotation}deg) scale(${transform.scale})`,
        transformOrigin: `50% ${(BASE_Y / SVG_H) * 100}%`,
        zIndex: 9999,
        pointerEvents: "auto",
        cursor: "grab",
        touchAction: "none",
        userSelect: "none",
        filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.25))",
      }}
      onPointerDown={onPointerDownMove}
    >
      <ProtractorSvg />
      <Tooltip title="Drag to rotate (hold Shift to snap to 1°)" placement="top">
        <Box
          onPointerDown={onPointerDownRotate}
          sx={{
            position: "absolute",
            left: -14,
            bottom: SVG_H - BASE_Y - 14,
            width: 28,
            height: 28,
            borderRadius: "50%",
            bgcolor: "#1565c0",
            color: "white",
            fontSize: "0.7rem",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "grab",
            border: "2px solid white",
          }}
        >
          ↻
        </Box>
      </Tooltip>
      <Tooltip title="Drag right/left to resize" placement="top">
        <Box
          onPointerDown={(e) => onPointerDownScale(e, { min: 0.5, max: 2.5, perPx: 0.006 })}
          sx={{
            position: "absolute",
            right: -14,
            bottom: SVG_H - BASE_Y - 14,
            width: 28,
            height: 28,
            borderRadius: "6px",
            bgcolor: "#0d47a1",
            color: "white",
            fontSize: "0.85rem",
            fontWeight: 800,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "ew-resize",
            border: "2px solid white",
          }}
        >
          ⇔
        </Box>
      </Tooltip>
    </Box>,
    document.body
  );
}
